import {
  FETCH_BATCH_PLACES,
  FETCH_HOTEL_POPULAR_HOLIDAY_TYPES,
  FETCH_PLACE,
  FETCH_PLACE_ANCESTORS,
  FETCH_PLACE_BY_COORDS,
  FETCH_PLACE_BY_NAME,
  FETCH_POPULAR_DESTINATIONS,
  FETCH_TOURS_POPULAR_DESTINATIONS,
  FETCH_TRENDING_DESTINATIONS,
  FETCH_POPULAR_FILTERS,
  FETCH_SPECIFC_PLACES,
  FETCH_DESTINATION_IMAGE,
  FETCH_GOOGLE_PLACE,
  FETCH_DESTINATION_TAGS,
} from 'actions/apiActionConstants'
import { createAppReducer, apiReducerSwitch } from 'lib/redux/reducerUtils'
import { API_CALL_FAILURE, API_CALL_REQUEST, API_CALL_SUCCESS } from 'actions/actionConstants'
import { arrayToObject } from 'lib/array/arrayUtils'
import {
  mergeReadyPlacesIntoLegacyMap,
  setPlaceCacheEntries,
} from 'lib/places/placeCacheUtils'

const initialState: App.DestinationState = {
  featuredDestinations: [],
  fetchingFeaturedDestinations: false,
  popularFilters: {
    locations: [],
    holidayTypes: [],
    amenities: [],
    inclusions: [],
    orderBy: {},
  },
  fetchingPopularFilters: false,
  areFeaturedDestinationsLoaded: false,
  locationToPlace: {},
  coordsToPlace: {},
  placesLoading: {},
  places: {},
  placeCacheByLocale: {},
  placeAncestors: {},
  fetchingAncestors: {},
  popularDestinations: {
    domestic: { fetching: false },
    international: { fetching: false },
  },
  trendingDestinations: [],
  fetchingTrendingDestinations: false,
  trendingDestinationsByUserKey: {},
  fetchingTrendingDestinationsByUserKey: {},
  fetchingHotelPopularHolidayTypes: false,
  hotelPopularHolidayTypes: [],
  specificPlaces: {},
  fetchingSpecificPlaces: {},
  fetchingDestinationImage: {},
  googlePlaces: {},
  destinationTags: {},
}

const apiCalls = apiReducerSwitch<App.DestinationState>({
  [FETCH_GOOGLE_PLACE]: {
    request: (state, action) => ({
      placesLoading: {
        ...state.placesLoading,
        [action.id]: true,
      },
    }),
    success: (state, action) => ({
      placesLoading: {
        ...state.placesLoading,
        [action.id]: false,
      },
      googlePlaces: {
        ...state.googlePlaces,
        [action.id]: action.data,
      },
    }),
    failure: (state, action) => ({
      placesLoading: {
        ...state.placesLoading,
        [action.id]: false,
      },
    }),
  },
  [FETCH_POPULAR_FILTERS]: {
    request: () => ({
      fetchingPopularFilters: true,
    }),
    success: (state, action) => ({
      popularFilters: action.data,
      fetchingPopularFilters: false,
    }),
    failure: () => ({
      fetchingPopularFilters: false,
    }),
  },
  [FETCH_PLACE_BY_NAME]: {
    request: (state, action) => ({
      placesLoading: {
        ...state.placesLoading,
        [action.location]: true,
      },
    }),
    success: (state, action) => ({
      locationToPlace: {
        ...state.locationToPlace,
        [action.location]: action.data,
      },
      placesLoading: {
        ...state.placesLoading,
        [action.location]: false,
      },
    }),
    failure: (state, action) => ({
      locationToPlace: {
        ...state.locationToPlace,
        [action.location]: null,
      },
      placesLoading: {
        ...state.placesLoading,
        [action.location]: false,
      },
    }),
  },
  [FETCH_BATCH_PLACES]: {
    request: (state, action) => ({
      placeCacheByLocale: setPlaceCacheEntries(
        state.placeCacheByLocale,
        action.locale,
        arrayToObject(action.ids, (id: string) => id, () => ({ status: 'loading' as const })),
      ),
    }),
    success: (state, action) => {
      const fetched = arrayToObject(action.data, p => p.id, p => p)
      const cacheUpdates: Record<string, App.PlaceCacheEntry> = {}
      for (const id of action.ids) {
        const place = fetched[id]
        cacheUpdates[id] = place ? { status: 'ready', place } : { status: 'missing' }
      }
      const placeCacheByLocale = setPlaceCacheEntries(state.placeCacheByLocale, action.locale, cacheUpdates)
      const places = mergeReadyPlacesIntoLegacyMap(state.places, action.locale, placeCacheByLocale, action.ids)
      return { placeCacheByLocale, places }
    },
    failure: (state, action) => ({
      placeCacheByLocale: setPlaceCacheEntries(
        state.placeCacheByLocale,
        action.locale,
        arrayToObject(action.ids, (id: string) => id, () => ({ status: 'error' as const })),
      ),
    }),
  },
  [FETCH_PLACE]: {
    request: (state, action) => ({
      placeCacheByLocale: setPlaceCacheEntries(
        state.placeCacheByLocale,
        action.locale,
        { [action.id]: { status: 'loading' } },
      ),
    }),
    success: (state, action) => {
      const cacheEntry: App.PlaceCacheEntry = action.data
        ? { status: 'ready', place: action.data }
        : { status: 'missing' }
      const placeCacheByLocale = setPlaceCacheEntries(state.placeCacheByLocale, action.locale, {
        [action.id]: cacheEntry,
      })
      const places = action.data
        ? { ...state.places, [action.id]: action.data }
        : state.places
      return {
        placeCacheByLocale,
        places,
      }
    },
    failure: (state, action) => ({
      placeCacheByLocale: setPlaceCacheEntries(
        state.placeCacheByLocale,
        action.locale,
        { [action.id]: { status: 'error' } },
      ),
    }),
  },
  [FETCH_PLACE_BY_COORDS]: {
    request: (state, action) => ({
      placesLoading: {
        ...state.placesLoading,
        [action.key]: true,
      },
    }),
    success: (state, action) => ({
      coordsToPlace: {
        ...state.coordsToPlace,
        [action.key]: action.data,
      },
      placesLoading: {
        ...state.placesLoading,
        [action.key]: false,
      },
    }),
    failure: (state, action) => ({
      coordsToPlace: {
        ...state.coordsToPlace,
        [action.key]: null,
      },
      placesLoading: {
        ...state.placesLoading,
        [action.key]: false,
      },
    }),
  },
  [FETCH_POPULAR_DESTINATIONS]: {
    request: (state, action) => ({
      popularDestinations: {
        ...state.popularDestinations,
        [action.destinationsType]: { fetching: true },
      },
    }),
    success: (state, action) => ({
      popularDestinations: {
        ...state.popularDestinations,
        [action.destinationsType]: { destinations: action.data, fetching: false },
      },
    }),
    failure: (state, action) => ({
      popularDestinations: {
        ...state.popularDestinations,
        [action.destinationsType]: { destinations: [], fetching: false },
      },
    }),
  },
  [FETCH_TRENDING_DESTINATIONS]: {
    request: (state, action) => {
      // Only update static fetching state for non-personalized requests (no user IDs present)
      // userKey format: 'leUserId_domainUserId_disablePersonalisation'
      // Non-personalized: 'anon_anon_true' or 'anon_anon_false'
      const isNonPersonalizedRequest = action.userKey.startsWith('anon_anon_')
      return {
        ...(isNonPersonalizedRequest && { fetchingTrendingDestinations: true }),
        fetchingTrendingDestinationsByUserKey: {
          ...state.fetchingTrendingDestinationsByUserKey,
          [action.userKey]: true,
        },
      }
    },
    success: (state, action) => {
      // Only update static list for non-personalized requests (no user IDs present)
      // userKey format: 'leUserId_domainUserId_useLereFlag'
      // Non-personalized: 'anon_anon_true' or 'anon_anon_false'
      const isNonPersonalizedRequest = action.userKey.startsWith('anon_anon_')
      return {
        ...(isNonPersonalizedRequest && {
          trendingDestinations: action.data,
          fetchingTrendingDestinations: false,
        }),
        trendingDestinationsByUserKey: {
          ...state.trendingDestinationsByUserKey,
          [action.userKey]: action.data,
        },
        fetchingTrendingDestinationsByUserKey: {
          ...state.fetchingTrendingDestinationsByUserKey,
          [action.userKey]: false,
        },
      }
    },
    failure: (state, action) => {
      // Only update static fetching state for non-personalized requests (no user IDs present)
      // userKey format: 'leUserId_domainUserId_useLereFlag'
      // Non-personalized: 'anon_anon_true' or 'anon_anon_false'
      const isNonPersonalizedRequest = action.userKey.startsWith('anon_anon_')
      return {
        ...(isNonPersonalizedRequest && { fetchingTrendingDestinations: false }),
        fetchingTrendingDestinationsByUserKey: {
          ...state.fetchingTrendingDestinationsByUserKey,
          [action.userKey]: false,
        },
      }
    },
  },
  [FETCH_HOTEL_POPULAR_HOLIDAY_TYPES]: {
    request: () => ({
      fetchingHotelPopularHolidayTypes: true,
    }),
    success: (state, action) => ({
      hotelPopularHolidayTypes: action.data.map((d: any) => d.name),
      fetchingHotelPopularHolidayTypes: false,
    }),
    failure: () => ({
      fetchingHotelPopularHolidayTypes: false,
    }),
  },
  [FETCH_TOURS_POPULAR_DESTINATIONS]: {
    request: (state) => ({
      tours: {
        popularDestinations: state.tours?.popularDestinations ?? [],
        isFetchingPopularDestinations: true,
      },
    }),
    success: (state, action) => ({
      tours: {
        isFetchingPopularDestinations: false,
        popularDestinations: action.data,
      },
    }),
    failure: () => ({
      tours: {
        popularDestinations: [],
        isFetchingPopularDestinations: false,
      },
    }),
  },
  [FETCH_PLACE_ANCESTORS]: {
    request: (state, action) => ({
      fetchingAncestors: {
        ...state.fetchingAncestors,
        [action.id]: true,
      },
    }),
    success: (state, action) => ({
      placeAncestors: {
        ...state.placeAncestors,
        [action.id]: action.data?.map(place => place.id) ?? [],
      },
      fetchingAncestors: {
        ...state.fetchingAncestors,
        [action.id]: false,
      },
      places: {
        ...state.places,
        ...arrayToObject(action.data, place => place.id),
      },
    }),
    failure: (state, action) => ({
      fetchingAncestors: {
        ...state.fetchingAncestors,
        [action.id]: false,
      },
    }),
  },
  [FETCH_SPECIFC_PLACES]: {
    request: (state, action) => ({
      fetchingSpecificPlaces: {
        ...state.fetchingSpecificPlaces,
        [action.placeId]: true,
      },
    }),
    success: (state, action) => ({
      fetchingSpecificPlaces: {
        ...state.fetchingSpecificPlaces,
        [action.placeId]: false,
      },
      specificPlaces: {
        ...state.specificPlaces,
        [action.placeId]: action.data,
      },
    }),
    failure: (state, action) => ({
      fetchingSpecificPlaces: {
        ...state.fetchingSpecificPlaces,
        [action.placeId]: false,
      },
      specificPlaces: {
        ...state.specificPlaces,
        [action.placeId]: [],
      },
    }),
  },
  [FETCH_DESTINATION_IMAGE]: {
    request: (state, action) => ({
      fetchingDestinationImage: {
        ...state.fetchingDestinationImage,
        [action.placeId]: true,
      },
    }),
    success: (state, action) => {
      const place = state.places[action.placeId]
      return ({
        fetchingDestinationImage: {
          ...state.fetchingDestinationImage,
          [action.placeId]: false,
        },
        places: {
          ...state.places,
          [action.placeId]: place ?
              { ...place, imageId: action.data } :
            place,
        },
      })
    },
    failure: (state, action) => {
      const place = state.places[action.placeId]
      return ({
        fetchingDestinationImage: {
          ...state.fetchingDestinationImage,
          [action.placeId]: false,
        },
        places: {
          ...state.places,
          [action.placeId]: place ?
              { ...place, imageId: undefined } :
            place,
        },
      })
    },
  },
  [FETCH_DESTINATION_TAGS]: {
    request: (state, action) => ({
      destinationTags: {
        ...state.destinationTags,
        [action.cacheKey]: {
          tags: [],
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      destinationTags: {
        ...state.destinationTags,
        [action.cacheKey]: {
          tags: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      destinationTags: {
        ...state.destinationTags,
        [action.cacheKey]: {
          tags: [],
          fetching: false,
        },
      },
    }),
  },
})

const destinationReducer = createAppReducer<App.DestinationState>(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
})

export default destinationReducer
